import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EvaluationTable } from "@/features/dashboard/components/evaluation-table";
import { useUpdateConversion } from "@/features/dashboard/queries/conversion.update.queries";
import { cn } from "@/lib/utils";
import { Batch } from "@/types/dashboard.types";
import { ArrowRight, Ban, Download, Loader2, X } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useExportBatchCsv } from "../../../queries/conversions.export.queries";
import { ReviewInterface } from "./review-batch";
import { PdfViewer } from "./pdf-viewer";

export function HistoryDetails({ data }: { data: Batch }) {
  const { mutateAsync: exportBatchCsv, isPending: isExporting } =
    useExportBatchCsv();

  const handleDownloadCsv = async () => {
    try {
      await exportBatchCsv(data.id);
    } catch (error) {
      console.error("Failed to download CSV:", error);
    }
  };

  return (
    <div>
      {data.status === "CONVERTING" ? (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-10 h-10 animate-spin" />
        </div>
      ) : data.status === "COMPLETED" ? (
        <div className="flex flex-col gap-4 p-4">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-bold flex items-center gap-2">
              <span>{data.countryName}</span>
              <span>, {data.scaleName}</span>
            </h1>
            <Button
              onClick={handleDownloadCsv}
              disabled={isExporting}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {isExporting ? "Generating..." : "Download Batch CSV"}
            </Button>
          </div>
          {data.documents.map(
            (doc) =>
              doc.status === "CONVERTED" && (
                <EvaluationTable
                  fileUrl={doc.fileUrl}
                  key={doc.id}
                  batchId={data.id}
                  countryId={data.countryId}
                  gradingScaleId={data.scaleId}
                  data={{
                    conversion: doc.verifiedData!.conversion,
                    studentInformation: doc.verifiedData!.studentInformation,
                    documentId: doc.id,
                  }}
                />
              )
          )}

          {/* Document Failure state */}
          {data.documents
            .filter((doc) => doc.status === "ERRORED")
            .map((doc) => (
              <ErrorState
                key={doc.id}
                documentName={doc.name}
                countryId={data.countryId}
                gradingScaleId={data.scaleId}
                scaleName={`${data.countryName}, ${data.scaleName}`}
                documentId={doc.id}
                batchId={data.id}
                fileName={doc.name}
                fileUrl={doc.fileUrl}
                className="md:p-5"
              />
            ))}
        </div>
      ) : (
        data.status !== "FAILED" && <ReviewInterface batch={data} />
      )}

      {/* Batch Failure state */}
      {data.status === "FAILED" && (
        <div className="flex flex-col justify-center items-center overflow-hidden p-16 w-full">
          <ErrorState
            documentName={data.documents[0].name}
            countryId={data.countryId}
            gradingScaleId={data.scaleId}
            documentId={data.documents[0].id}
            batchId={data.id}
            fileName={data.documents[0].name}
            fileUrl={data.documents[0].fileUrl}
            scaleName={`${data.countryName}, ${data.scaleName}`}
          />
        </div>
      )}
    </div>
  );
}

function ErrorState({
  documentName,
  countryId,
  gradingScaleId,
  documentId,
  batchId,
  className,
  fileName,
  scaleName,
  fileUrl,
}: {
  documentName: string;
  countryId: string;
  gradingScaleId: string;
  documentId: string;
  batchId: string;
  className?: string;
  fileName: string;
  fileUrl: string;
  scaleName: string;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Empty className={cn("h-full border", className)}>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Ban className="text-red-500" />
        </EmptyMedia>
        <EmptyTitle>Extraction Incomplete</EmptyTitle>
        <EmptyDescription>
          You can manually complete the conversion.
        </EmptyDescription>
        <EmptyDescription className="underline">
          {documentName}
        </EmptyDescription>
        <EmptyContent>
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <ArrowRight />
                Convert Manually
              </Button>
            </DialogTrigger>
            <ManualMarksDialog
              fileUrl={fileUrl}
              fileName={fileName}
              countryId={countryId}
              gradingScaleId={gradingScaleId}
              documentId={documentId}
              batchId={batchId}
              setIsOpen={setIsOpen}
              scaleName={scaleName}
            />
          </Dialog>
        </EmptyContent>
      </EmptyHeader>
    </Empty>
  );
}

function ManualMarksDialog({
  countryId,
  gradingScaleId,
  scaleName,
  documentId,
  batchId,
  fileUrl,
  fileName,
  setIsOpen,
}: {
  countryId: string;
  gradingScaleId: string;
  scaleName: string;
  documentId: string;
  batchId: string;
  fileUrl: string;
  fileName: string;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  type Row = { id: number; course: string; credits: string; grade: string };

  const [rows, setRows] = useState<Row[]>(() =>
    Array.from({ length: 5 }, (_, i) => ({
      id: i + 1,
      course: "",
      credits: "",
      grade: "",
    }))
  );
  const [rowsToAdd, setRowsToAdd] = useState(1);
  const nextIdRef = useRef(6);

  const conversion = useMemo(
    () => ({
      table: rows.map((r) => ({
        subject: r.course,
        score: r.grade,
        credits: r.credits,
        points: 0,
      })),
      conclusion: "",
      cumulativeGPA: "",
    }),
    [rows]
  );

  const { mutateAsync: updateConversion, isPending } = useUpdateConversion({
    documentId,
    batchId,
    conversion,
    countryId,
    gradingScaleId,
    isFailed: true,
  });

  const handleAddRows = () => {
    const newRows = Array.from({ length: rowsToAdd }, (_, i) => ({
      id: nextIdRef.current + i,
      course: "",
      credits: "",
      grade: "",
    }));
    nextIdRef.current += rowsToAdd;
    setRows((prev) => [...prev, ...newRows]);
  };

  const handleDeleteRow = (id: number) => {
    if (rows.length <= 1) {
      toast.warning("Cannot delete the last row");
      return;
    }
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleInputChange = (id: number, field: keyof Row, value: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowIndex: number,
    field: "course" | "credits" | "grade"
  ) => {
    const { key } = e;
    let targetElement: HTMLInputElement | null = null;

    if (key === "ArrowUp") {
      e.preventDefault();
      if (rowIndex > 0) {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex - 1}'][data-field='${field}']`
        );
      }
    } else if (key === "ArrowDown") {
      e.preventDefault();
      if (rowIndex < rows.length - 1) {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex + 1}'][data-field='${field}']`
        );
      }
    } else if (key === "ArrowLeft") {
      e.preventDefault();
      if (field === "grade") {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex}'][data-field='credits']`
        );
      } else if (field === "credits") {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex}'][data-field='course']`
        );
      } else if (field === "course" && rowIndex > 0) {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex - 1}'][data-field='grade']`
        );
      }
    } else if (key === "ArrowRight") {
      e.preventDefault();
      if (field === "course") {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex}'][data-field='credits']`
        );
      } else if (field === "credits") {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex}'][data-field='grade']`
        );
      } else if (field === "grade" && rowIndex < rows.length - 1) {
        targetElement = document.querySelector(
          `[data-row-index='${rowIndex + 1}'][data-field='course']`
        );
      }
    }

    if (targetElement) {
      targetElement.focus();
      targetElement.select();
    }
  };

  const handleClearAll = () => {
    nextIdRef.current = 6;
    setRowsToAdd(1);
    setRows(
      Array.from({ length: 5 }, (_, i) => ({
        id: i + 1,
        course: "",
        credits: "",
        grade: "",
      }))
    );
    toast.success("All rows cleared");
  };

  const handleSubmit = async () => {
    if (rows.some((r) => !r.course || !r.grade || !r.credits)) {
      toast.error("Please fill all the fields");
      return;
    }
    if (rows.some((r) => Number(r.credits) < 1)) {
      toast.error("Credits cannot be less than 1");
      return;
    }

    try {
      await updateConversion();
      setIsOpen(false);
      toast.success("Conversion updated successfully");
    } catch (e) {
      toast.error("Failed to update conversion");
    }
  };

  return (
    <DialogContent className="sm:max-w-5xl">
      <DialogHeader>
        <DialogTitle>Convert Manually</DialogTitle>
        <DialogDescription>{fileName}</DialogDescription>
        <DialogDescription>Scale: {scaleName}</DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-3 gap-2">
        <div
          className={cn("space-y-4 col-span-1", {
            "col-span-2": fileUrl.length,
          })}
        >
          <div className="border max-h-[50vh] overflow-y-auto rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="divide-x hover:bg-muted/50">
                  <TableHead className="text-center">#</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead className="w-20">Credits</TableHead>
                  <TableHead className="w-22">Grade</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="divide-y divide-border">
                {rows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    className="hover:bg-muted/50 divide-x divide-border"
                  >
                    <TableCell className="text-center p-0">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <Input
                        disabled={isPending}
                        value={row.course}
                        onChange={(e) =>
                          handleInputChange(row.id, "course", e.target.value)
                        }
                        onKeyDown={(e) => handleKeyDown(e, index, "course")}
                        data-row-index={index}
                        data-field="course"
                        className="border-0 bg-transparent"
                      />
                    </TableCell>
                    <TableCell className="w-fit">
                      <Input
                        disabled={isPending}
                        value={row.credits}
                        onChange={(e) =>
                          handleInputChange(row.id, "credits", e.target.value)
                        }
                        onKeyDown={(e) => handleKeyDown(e, index, "credits")}
                        data-row-index={index}
                        data-field="credits"
                        className="border-0 bg-transparent"
                        type="number"
                      />
                    </TableCell>
                    <TableCell className="w-fit">
                      <Input
                        disabled={isPending}
                        value={row.grade}
                        onChange={(e) =>
                          handleInputChange(row.id, "grade", e.target.value)
                        }
                        onKeyDown={(e) => handleKeyDown(e, index, "grade")}
                        data-row-index={index}
                        data-field="grade"
                        className="border-0 bg-transparent"
                      />
                    </TableCell>
                    <TableCell className="text-center p-0">
                      <Button
                        variant="ghost"
                        disabled={isPending}
                        size="sm"
                        onClick={() => handleDeleteRow(row.id)}
                        className="size-4 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="mt-2 flex items-center gap-4">
            <span className="text-sm font-medium">Number of rows to add:</span>
            <div className="flex items-center">
              <Input
                type="number"
                min="1"
                value={rowsToAdd}
                disabled={isPending}
                onChange={(e) => setRowsToAdd(Number(e.target.value))}
                className="w-16"
              />
              <Button variant="ghost" onClick={handleAddRows} className="ml-2">
                Add
              </Button>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={handleClearAll}
              disabled={isPending}
            >
              Clear
            </Button>
            <Button
              className="px-6"
              onClick={handleSubmit}
              disabled={isPending}
            >
              {isPending ? "Saving..." : "Save Conversion"}
            </Button>
          </div>
        </div>

        {fileUrl && fileUrl.length > 0 && <PdfViewer source={fileUrl} />}
      </div>
    </DialogContent>
  );
}
